/**
 * COUCHE DE COMPATIBILITÉ - Palette Admin
 *
 * Les couleurs sont maintenant définies dans le thème racine.
 * Ce fichier sert de couche de compatibilité pour les imports existants.
 *
 * @deprecated Les nouveaux composants doivent utiliser useTheme() pour accéder aux couleurs
 * @module admin/palette
 * @author Équipe Brydge
 * @since 2.5.0
 */

// Import des couleurs depuis le thème racine
import {
  NAVY,
  PRIMARY,
  SECONDARY,
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
  PURPLE,
  PINK,
  TEAL,
  INDIGO,
  SLATE,
  AMBER,
  CYAN,
  SKY,
  BLUE,
  ROSE,
} from '../palette';

// ============================================================================
// COULEURS - Re-export depuis le thème racine
// ============================================================================

/**
 * Couleurs sémantiques standard
 * Re-exportées depuis le thème racine pour compatibilité
 */
const SEMANTIC_COLORS = {
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
};

/**
 * Couleurs étendues
 * Re-exportées depuis le thème racine pour compatibilité
 */
const EXTENDED_COLORS = {
  purple: PURPLE,
  pink: PINK,
  teal: TEAL,
  indigo: INDIGO,
  slate: SLATE,
  amber: AMBER,
  cyan: CYAN,
  sky: SKY,
  blue: BLUE,
  rose: ROSE,
};

// ============================================================================
// CONFIGURATION NAVIGATION - Spécifique au panel admin
// ============================================================================

/**
 * Configuration des couleurs de navigation
 * Utilisées dans la sidebar et les éléments de navigation admin
 */
const NAV_CONFIG = {
  background: NAVY[900],
  backgroundOverlay: 'rgba(15, 28, 46, 0.95)',
  border: 'rgba(255, 255, 255, 0.08)',
  divider: 'rgba(255, 255, 255, 0.1)',
};

/**
 * Configuration des couleurs de texte pour fond sombre
 */
const TEXT_CONFIG = {
  primary: '#E2E8F0',
  primaryMuted: 'rgba(226, 232, 240, 0.7)',
  primarySoft: 'rgba(226, 232, 240, 0.8)',
  secondary: '#94A3B8',
  secondaryMuted: 'rgba(148, 163, 184, 0.8)',
};

/**
 * Configuration des couleurs d'accent (états actifs)
 */
const ACCENT_CONFIG = {
  main: SEMANTIC_COLORS.primary.main,
  light: SEMANTIC_COLORS.primary.light,
  bg5: 'rgba(59, 130, 246, 0.05)',
  bg10: 'rgba(59, 130, 246, 0.1)',
  bg15: 'rgba(59, 130, 246, 0.15)',
  bg20: 'rgba(59, 130, 246, 0.2)',
  bg30: 'rgba(59, 130, 246, 0.3)',
  bg40: 'rgba(59, 130, 246, 0.4)',
  bg50: 'rgba(59, 130, 246, 0.5)',
};

/**
 * Configuration des états interactifs
 */
const INTERACTIVE_CONFIG = {
  hoverSoft: 'rgba(255, 255, 255, 0.05)',
  hoverMedium: 'rgba(255, 255, 255, 0.08)',
};

/**
 * Configuration des ombres
 */
const SHADOW_CONFIG = {
  card: '0 4px 20px rgba(59, 130, 246, 0.15)',
};

// ============================================================================
// EXPORTS PRINCIPAUX
// ============================================================================

/**
 * Toutes les couleurs disponibles (sémantiques + étendues)
 * Re-exportées depuis le thème racine pour compatibilité
 *
 * @example
 * import { COLORS } from 'packages/common/theme/admin';
 * color: COLORS.purple.main
 * bgcolor: COLORS.teal.lighter
 */
export const COLORS = {
  ...SEMANTIC_COLORS,
  ...EXTENDED_COLORS,
};

/**
 * Liste des clés de couleurs pour itération et validation
 *
 * @example
 * COLOR_KEYS.forEach(color => console.log(COLORS[color].main));
 */
export const COLOR_KEYS = Object.keys(COLORS);

/**
 * Échelle Navy complète pour usages avancés
 * Re-exportée depuis le thème racine pour compatibilité
 *
 * @example
 * import { ADMIN_NAVY } from 'packages/common/theme/admin';
 * bgcolor: ADMIN_NAVY[800]
 */
export const ADMIN_NAVY = NAVY;

/**
 * Palette de navigation admin (rétro-compatible avec l'ancien format)
 * Contient les configurations pour nav, text, accent
 *
 * @example
 * import { ADMIN_PALETTE } from 'packages/common/theme/admin';
 * bgcolor: ADMIN_PALETTE.nav.background
 */
export const ADMIN_PALETTE = {
  nav: NAV_CONFIG,
  text: TEXT_CONFIG,
  accent: ACCENT_CONFIG,
  interactive: INTERACTIVE_CONFIG,
  shadow: SHADOW_CONFIG,
};

/**
 * Couleurs de base exportées séparément pour rétro-compatibilité
 * Maintient la compatibilité avec le code existant
 *
 * @deprecated Préférer l'utilisation de COLORS ou ADMIN_PALETTE
 */
export const ADMIN_BASE_COLORS = {
  navy: NAVY[900],
  bluePrimary: SEMANTIC_COLORS.primary.main,
  blueLight: SEMANTIC_COLORS.primary.light,
  textLight: TEXT_CONFIG.primary,
  textMuted: TEXT_CONFIG.secondary,
};
